/**
 *  将新元素放在目标元素的后面
 *  @param {HTMLElement} newEl - 新元素
 *  @param {HTMLElement} targetEl - 目标元素
 *  @return {void}
 */
function insertAfter(newEl, targetEl) {
  const parent = targetEl.parentElement;
  if (parent.lastChild === targetEl) {
    parent.appendChild(newEl);
  } else {
    parent.insertBefore(newEl, targetEl.nextSibling);
  }
}

/**
 *  设置元素的属性
 *  @param {HTMLElement} el - 绑定元素
 *  @return {void}
 */
function setBound(el) {
  const computedStyle = window.getComputedStyle(el);
  const topValue = parseFloat(computedStyle.top);
  const bottomValue = parseFloat(computedStyle.bottom);
  const elementHeight = el.getBoundingClientRect().height;
  el._watchStickyHasTop = !isNaN(topValue);
  el._watchStickyHasBottom = !isNaN(bottomValue);
  el._watchStickyTopVal = isNaN(topValue) ? 0 : topValue;
  el._watchStickyBottomVal = isNaN(bottomValue) ? 0 : bottomValue;
  el._watchStickyOldHeight = el._watchStickyHeight || elementHeight;
  el._watchStickyHeight = elementHeight;
  el._watchStickyOldscrollHeight = el._watchStickyRoot.scrollHeight;
  const prev = el.previousSibling;
  const next = el.nextSibling;
  if (prev && prev.classList.contains('_sticky_top')) {
    prev.style.cssText = `position: relative;top: -${el._watchStickyTopVal}px;`;
  }
  if (next && next.classList.contains('_sticky_bottom')) {
    next.style.cssText = `position: relative;top: ${el._watchStickyBottomVal}px;`;
  }
}

/**
 *  绑定IntersectionObserver
 *  @param {HTMLElement} el - 绑定元素
 *  @param {number} threshold - 监听目标与边界盒交叉区域的比例值
 *  @param {Function} stickyChangeFn - 执行事件回调  true处于sticky状态
 *  @return {void}
 */
function bindIntersectionObserver(el, threshold, stickyChangeFn) {
  const root = el._watchStickyRootIsNull ? null : el._watchStickyRoot;
  let prev = el.previousSibling;
  let next = el.nextSibling;
  if (prev && !prev.classList.contains('_sticky_top')) {
    prev = null;
  }
  if (next && !next.classList.contains('_sticky_bottom')) {
    next = null;
  }
  if (prev) {
    if (el._watchStickytopOb) {
      el._watchStickytopOb.disconnect();
    }
    el._watchStickytopOb = new IntersectionObserver(([entry]) => {
      const {
        boundingClientRect, rootBounds
      } = entry;
      if (el._watchStickyHasTop) {
        if (el._watchStickyTopVal + el._watchStickyBottomVal >= rootBounds.height) {
          el._watchStickyBottomVal = 0;
          el._watchStickyHasBottom = false;
        }
        if (!el._watchStickyHasBottom) {
          if (Math.ceil(boundingClientRect.top) < Math.ceil(rootBounds.top)) {
            stickyChangeFn && stickyChangeFn(true);
          } else {
            stickyChangeFn && stickyChangeFn(false);
          }
        } else if (el._watchStickyHasBottom) {
          if (Math.ceil(boundingClientRect.top) < el._watchStickyTopVal + Math.ceil(rootBounds.top)) {
            if (Math.ceil(boundingClientRect.top) < Math.ceil(rootBounds.top)) {
              stickyChangeFn && stickyChangeFn(true);
            } else {
              stickyChangeFn && stickyChangeFn(false);
            }
          }
        }
      }
    }, {threshold, root});
    el._watchStickytopOb.observe(prev);
  }
  if (next) {
    if (el._watchStickybottomOb) {
      el._watchStickybottomOb.disconnect();
    }
    el._watchStickybottomOb = new IntersectionObserver(([entry]) => {
      if (el._watchStickyHasBottom) {
        const {
          boundingClientRect, rootBounds
        } = entry;
        if (!el._watchStickyHasTop) {
          if (el._watchStickyIsScrollBar && Math.ceil(boundingClientRect.bottom) > Math.ceil(rootBounds.bottom)) {
            stickyChangeFn && stickyChangeFn(true);
          } else {
            stickyChangeFn && stickyChangeFn(false);
          }
        } else if (el._watchStickyHasTop) {
          if (Math.ceil(boundingClientRect.bottom) > el._watchStickyBottomVal + Math.ceil(rootBounds.top)) {
            if (el._watchStickyIsScrollBar && Math.ceil(boundingClientRect.bottom) > Math.ceil(rootBounds.bottom)) {
              stickyChangeFn && stickyChangeFn(true);
            } else {
              stickyChangeFn && stickyChangeFn(false);
            }
          }
        }
      }
    }, {threshold: 0, root});
    el._watchStickybottomOb.observe(next);
  }
}

/**
 *  监听元素是否处于sticky状态
 *  @param {Function} stickyChangeFn - 执行事件回调  true处于sticky状态
 *  @example
 *  例：<div v-watchSticky="stickyChangeFn">提交</button>
 */
const watchSticky = {
  inserted: function (el, binding) {
    const stickyChangeFn = binding.value;
    let root = el.parentElement;
    while (root.parentElement && !['auto', 'hidden', 'overlay', 'scroll'].includes(window.getComputedStyle(root).overflowY)) {
      root = root.parentElement;
    }
    el._watchStickyRootIsNull = false;
    let isScrollBar = true;
    if (root === document.body) {
      el._watchStickyRoot = root;
      el._watchStickyRootIsNull = true;
      root = null;
    } else {
      el._watchStickyRoot = root;
      isScrollBar = root.scrollHeight > root.offsetHeight;
    }
    el._watchStickyIsScrollBar = isScrollBar;
    const parent = el.parentElement;
    const topSentinel = document.createElement('div');
    topSentinel.classList.add('_sticky_top');
    parent.insertBefore(topSentinel, el);
    const bottomSentinel = document.createElement('div');
    bottomSentinel.classList.add('_sticky_bottom');
    insertAfter(bottomSentinel, el);
    setBound(el);
    // 只有top时，以top为准，
    // 只有bottom时，以bottom为准
    // top bottom都存在并且不冲突时，以共同作用为准
    // top bottom都存在并且冲突(top 90%， bottom 90%)时，以top为准
    bindIntersectionObserver(el, 0, stickyChangeFn);
  },
  update: function (el, binding) {
    setTimeout(() => {
      let isChange = false;
      if (!el._watchStickyRootIsNull) {
        el._watchStickyIsScrollBar = el._watchStickyRoot.scrollHeight > el._watchStickyRoot.offsetHeight;
      }
      if (el._watchStickyRoot.scrollHeight !== el._watchStickyOldscrollHeight) {
        isChange = true;
      }
      setBound(el);
      if (el._watchStickyOldHeight !== el._watchStickyHeight) {
        isChange = true;
      }
      if (isChange) {
        const stickyChangeFn = binding.value;
        bindIntersectionObserver(el, 0, stickyChangeFn);
      }
    });
  },
  unbind(el) {
    el._watchStickytopOb.disconnect();
    el._watchStickybottomOb.disconnect();
    el._watchStickytopOb = null;
    el._watchStickybottomOb = null;
    el._watchStickyRoot = null;
    el._watchStickyIsScrollBar = null;
    el._watchStickyHasTop = null;
    el._watchStickyHasBottom = null;
    el._watchStickyTopVal = null;
    el._watchStickyBottomVal = null;
    el._watchStickyOldHeight = null;
    el._watchStickyHeight = null;
    el._watchStickyOldscrollHeight = null;
    el._watchStickyRootIsNull = null;
  }
};

export default watchSticky;
